/**
 * Переводы админ-раздела «Пользователи» и «Приглашения» (DEV-1267).
 * Подмешиваются в базовые локали через deepMerge в locales/index.ts.
 */
type LocaleOverride = Record<string, unknown>

type Locale = 'ru' | 'en' | 'vn'

const build = (l: Locale): LocaleOverride => ({
  common: {
    navigation: {
      adminUsers: { ru: 'Пользователи', en: 'Users', vn: 'Người dùng' }[l],
      adminInvitations: { ru: 'Приглашения', en: 'Invitations', vn: 'Lời mời' }[l],
    },
  },
  admin: {
    // Приложения из roles.apps (ADR-0042). Ключи — значения CHECK-ограничения.
    apps: {
      crm: { ru: 'crm', en: 'crm', vn: 'crm' }[l],
      agent_cabinet: { ru: 'кабинет агента', en: 'agent cabinet', vn: 'cổng đại lý' }[l],
      admin: { ru: 'админка', en: 'admin', vn: 'quản trị' }[l],
      docs: { ru: 'документация API', en: 'API docs', vn: 'tài liệu API' }[l],
    },
    // Названия ролей. Сервер отдаёт машинный слаг (`{key:"HOS", name:"hos"}`),
    // поэтому подписи живут здесь до DEV-1285 — локализации справочника.
    roles: {
      ADMIN: { ru: 'Администратор', en: 'Administrator', vn: 'Quản trị viên' }[l],
      ADMIN_USER: {
        ru: 'Администратор пользователей',
        en: 'User administrator',
        vn: 'Quản trị người dùng',
      }[l],
      AGENT: { ru: 'Агент', en: 'Agent', vn: 'Đại lý' }[l],
      CEO: { ru: 'Директор', en: 'CEO', vn: 'Giám đốc' }[l],
      HOS: {
        ru: 'Руководитель отдела продаж',
        en: 'Head of sales',
        vn: 'Trưởng phòng kinh doanh',
      }[l],
      MANAGER: { ru: 'Менеджер', en: 'Manager', vn: 'Quản lý' }[l],
      MARKETER: { ru: 'Маркетолог', en: 'Marketer', vn: 'Nhân viên marketing' }[l],
      PARTNER: { ru: 'Партнёр', en: 'Partner', vn: 'Đối tác' }[l],
      PROJECT_MANAGER: {
        ru: 'Менеджер проектов',
        en: 'Project manager',
        vn: 'Quản lý dự án',
      }[l],
      SUPPORT_MANAGER: {
        ru: 'Менеджер поддержки',
        en: 'Support manager',
        vn: 'Quản lý hỗ trợ',
      }[l],
      CONTENT_AUTHOR: { ru: 'Автор статей', en: 'Content author', vn: 'Tác giả bài viết' }[l],
      SERVICE_INTERNAL: {
        ru: 'Служебная: внутренние вызовы',
        en: 'Service: internal calls',
        vn: 'Dịch vụ: gọi nội bộ',
      }[l],
      SERVICE_RECONCILE: {
        ru: 'Служебная: сверка данных',
        en: 'Service: reconciliation',
        vn: 'Dịch vụ: đối soát',
      }[l],
    },
    users: {
      title: { ru: 'Пользователи', en: 'Users', vn: 'Người dùng' }[l],
      subtitle: {
        ru: 'Управление сотрудниками, ролями и доступом',
        en: 'Manage staff, roles and access',
        vn: 'Quản lý nhân sự, vai trò và quyền truy cập',
      }[l],
      invite: { ru: 'Пригласить', en: 'Invite', vn: 'Mời' }[l],
      hint: {
        ru: 'Щит у ключа роли — системная роль: правится только миграцией. Столбец «Приложения» производный от ролей и не редактируется.',
        en: 'A shield next to the role key means a system role: it is changed by migration only. The «Apps» column is derived from roles and is not editable.',
        vn: 'Khiên cạnh khóa vai trò nghĩa là vai trò hệ thống: chỉ đổi được bằng migration. Cột «Ứng dụng» suy ra từ vai trò và không sửa được.',
      }[l],
      today: { ru: 'сегодня', en: 'today', vn: 'hôm nay' }[l],
      yesterday: { ru: 'вчера', en: 'yesterday', vn: 'hôm qua' }[l],
      empty: {
        title: { ru: 'Ничего не найдено', en: 'Nothing found', vn: 'Không tìm thấy' }[l],
        description: {
          ru: 'Пользователей пока нет. Пригласите сотрудника по почте — пароль он придумает сам.',
          en: 'There are no users yet. Invite a member by email — they set their own password.',
          vn: 'Chưa có người dùng. Hãy mời nhân sự qua email — họ tự đặt mật khẩu.',
        }[l],
        filtered: {
          ru: 'По текущему запросу и фильтрам нет пользователей. Измените условия поиска или сбросьте фильтры.',
          en: 'No users match the current search and filters. Change the conditions or reset the filters.',
          vn: 'Không có người dùng khớp với tìm kiếm và bộ lọc hiện tại. Hãy đổi điều kiện hoặc đặt lại bộ lọc.',
        }[l],
        action: { ru: 'Сбросить фильтры', en: 'Reset filters', vn: 'Đặt lại bộ lọc' }[l],
      },
      searchPlaceholder: {
        ru: 'Имя или почта',
        en: 'Name or email',
        vn: 'Tên hoặc email',
      }[l],
      truncated: {
        ru: 'Показаны не все пользователи: список слишком длинный. Уточните поиск.',
        en: 'Not all users are shown: the list is too long. Narrow the search.',
        vn: 'Chưa hiển thị hết người dùng: danh sách quá dài. Hãy thu hẹp tìm kiếm.',
      }[l],
      confirmDelete: {
        title: { ru: 'Удалить?', en: 'Delete?', vn: 'Xóa?' }[l],
        text: {
          ru: 'Учётная запись {target} будет заблокирована, роли и связи сняты. Отменить это из интерфейса нельзя.',
          en: 'The account {target} will be blocked and its roles and links removed. This cannot be undone from the interface.',
          vn: 'Tài khoản {target} sẽ bị khóa, vai trò và liên kết bị gỡ. Không thể hoàn tác từ giao diện.',
        }[l],
      },
      columns: {
        user: { ru: 'Пользователь', en: 'User', vn: 'Người dùng' }[l],
        roles: { ru: 'Роли', en: 'Roles', vn: 'Vai trò' }[l],
        status: { ru: 'Статус', en: 'Status', vn: 'Trạng thái' }[l],
        apps: { ru: 'Приложения', en: 'Apps', vn: 'Ứng dụng' }[l],
        lastLogin: { ru: 'Последний вход', en: 'Last login', vn: 'Lần đăng nhập cuối' }[l],
        created: { ru: 'Создан', en: 'Created', vn: 'Ngày tạo' }[l],
      },
      status: {
        active: { ru: 'Активен', en: 'Active', vn: 'Hoạt động' }[l],
        inactive: { ru: 'Заблокирован', en: 'Blocked', vn: 'Bị khóa' }[l],
        pending: { ru: 'Приглашён', en: 'Invited', vn: 'Đã mời' }[l],
      },
      filters: {
        search: { ru: 'Поиск', en: 'Search', vn: 'Tìm kiếm' }[l],
        anyRole: { ru: 'Все роли', en: 'All roles', vn: 'Mọi vai trò' }[l],
        anyStatus: { ru: 'Все статусы', en: 'All statuses', vn: 'Mọi trạng thái' }[l],
        reset: { ru: 'Сбросить', en: 'Reset', vn: 'Đặt lại' }[l],
      },
      actions: {
        block: { ru: 'Заблокировать', en: 'Block', vn: 'Khóa' }[l],
        unblock: { ru: 'Разблокировать', en: 'Unblock', vn: 'Mở khóa' }[l],
        more: { ru: 'Ещё действия', en: 'More actions', vn: 'Thêm thao tác' }[l],
      },
      form: {
        editTitle: { ru: 'Правка пользователя', en: 'Edit user', vn: 'Sửa người dùng' }[l],
        email: { ru: 'Почта', en: 'Email', vn: 'Email' }[l],
        firstName: { ru: 'Имя', en: 'First name', vn: 'Tên' }[l],
        lastName: { ru: 'Фамилия', en: 'Last name', vn: 'Họ' }[l],
        roles: { ru: 'Роли', en: 'Roles', vn: 'Vai trò' }[l],
        rolesElsewhere: {
          ru: 'Роли меняются на вкладке «Роли»: там они назначаются по одной и запись в журнале остаётся точечной.',
          en: 'Roles are changed on the Roles tab: there they are assigned one at a time and the audit entry stays precise.',
          vn: 'Vai trò được đổi ở tab «Vai trò»: ở đó gán từng vai trò một và bản ghi nhật ký giữ được độ chính xác.',
        }[l],
        emailRequired: { ru: 'Укажите почту', en: 'Email is required', vn: 'Cần nhập email' }[l],
      },
      detail: {
        tabs: {
          profile: { ru: 'Профиль', en: 'Profile', vn: 'Hồ sơ' }[l],
          roles: { ru: 'Роли', en: 'Roles', vn: 'Vai trò' }[l],
          permissions: {
            ru: 'Эффективные права',
            en: 'Effective permissions',
            vn: 'Quyền hiệu lực',
          }[l],
          history: { ru: 'История', en: 'History', vn: 'Lịch sử' }[l],
        },
        assignedRoles: { ru: 'Роли пользователя', en: 'User roles', vn: 'Vai trò người dùng' }[l],
        addRole: {
          ru: 'Начните вводить название роли…',
          en: 'Start typing a role name…',
          vn: 'Bắt đầu nhập tên vai trò…',
        }[l],
        noRoles: { ru: 'Ролей нет', en: 'No roles', vn: 'Chưa có vai trò' }[l],
        rolesHint: {
          ru: 'Системная роль (со щитом) снимается только миграцией — крестика у чипа нет. Права выдаются только через роли.',
          en: 'A system role (with the shield) can only be removed by a migration — its chip has no cross. Permissions are granted through roles only.',
          vn: 'Vai trò hệ thống (có khiên) chỉ gỡ được bằng migration — chip của nó không có dấu X. Quyền chỉ được cấp qua vai trò.',
        }[l],
        individual: {
          title: {
            ru: 'Индивидуальные права',
            en: 'Individual permissions',
            vn: 'Quyền riêng lẻ',
          }[l],
          notice: {
            ru: 'Индивидуальные права выдаются только служебным и партнёрским принципалам. Для людей раздел read-only.',
            en: 'Individual permissions are for service and partner principals only. For people this block is read-only.',
            vn: 'Quyền riêng lẻ chỉ dành cho tài khoản dịch vụ và đối tác. Với người dùng, mục này chỉ đọc.',
          }[l],
          empty: {
            ru: 'Индивидуальных прав нет',
            en: 'No individual permissions',
            vn: 'Không có quyền riêng lẻ',
          }[l],
        },
        effective: {
          title: { ru: 'Эффективные права', en: 'Effective permissions', vn: 'Quyền hiệu lực' }[l],
          readOnly: { ru: 'read-only', en: 'read-only', vn: 'read-only' }[l],
          hint: {
            ru: 'Итог по всем ролям. Отсюда права не редактируются — только через роли.',
            en: 'The total across all roles. Not editable here — only through roles.',
            vn: 'Tổng hợp theo mọi vai trò. Không sửa được ở đây — chỉ qua vai trò.',
          }[l],
          viaRole: { ru: 'через роль {role}', en: 'via role {role}', vn: 'qua vai trò {role}' }[l],
          individual: { ru: 'индивидуально', en: 'individual', vn: 'riêng lẻ' }[l],
          denied: { ru: 'нет права', en: 'no permission', vn: 'không có quyền' }[l],
        },
        history: {
          hint: {
            ru: 'Изменения ролевой модели, затронувшие этого человека: и правки учётки, и выдача с снятием ролей.',
            en: 'Role-model changes that concern this person: account edits as well as role grants and revocations.',
            vn: 'Các thay đổi mô hình vai trò liên quan đến người này: sửa tài khoản, cấp và gỡ vai trò.',
          }[l],
          empty: { ru: 'Записей нет', en: 'No entries', vn: 'Không có bản ghi' }[l],
          columns: {
            date: { ru: 'Когда', en: 'When', vn: 'Khi nào' }[l],
            actor: { ru: 'Кто', en: 'Who', vn: 'Ai' }[l],
            action: { ru: 'Действие', en: 'Action', vn: 'Thao tác' }[l],
            target: { ru: 'Объект', en: 'Object', vn: 'Đối tượng' }[l],
          },
          actions: {
            ROLE_ASSIGN: { ru: 'Роль назначена', en: 'Role assigned', vn: 'Đã gán vai trò' }[l],
            ROLE_REVOKE: { ru: 'Роль снята', en: 'Role revoked', vn: 'Đã gỡ vai trò' }[l],
            USER_PERMISSION_GRANT: {
              ru: 'Право выдано',
              en: 'Permission granted',
              vn: 'Đã cấp quyền',
            }[l],
            USER_PERMISSION_REVOKE: {
              ru: 'Право снято',
              en: 'Permission revoked',
              vn: 'Đã thu hồi quyền',
            }[l],
            INVITATION_CREATE: {
              ru: 'Приглашение создано',
              en: 'Invitation created',
              vn: 'Đã tạo lời mời',
            }[l],
            INVITATION_REVOKE: {
              ru: 'Приглашение отозвано',
              en: 'Invitation revoked',
              vn: 'Đã thu hồi lời mời',
            }[l],
          },
        },
        footer: {
          ru: 'Изменения вступают в силу в течение 30 секунд',
          en: 'Changes take effect within 30 seconds',
          vn: 'Thay đổi có hiệu lực trong vòng 30 giây',
        }[l],
      },
      toast: {
        updated: { ru: 'Изменения сохранены', en: 'Changes saved', vn: 'Đã lưu thay đổi' }[l],
        deleted: { ru: 'Пользователь удалён', en: 'User deleted', vn: 'Đã xóa người dùng' }[l],
        statusChanged: { ru: 'Статус изменён', en: 'Status changed', vn: 'Đã đổi trạng thái' }[l],
        roleAssigned: { ru: 'Роль назначена', en: 'Role assigned', vn: 'Đã gán vai trò' }[l],
        roleRemoved: { ru: 'Роль снята', en: 'Role removed', vn: 'Đã gỡ vai trò' }[l],
      },
    },
    portfolio: {
      title: {
        ru: 'Сначала передайте портфель',
        en: 'Hand over the portfolio first',
        vn: 'Hãy bàn giao danh mục trước',
      }[l],
      lead: {
        ru: 'У этих сотрудников есть клиенты. Если действие выполнить сейчас, клиенты останутся без ответственного и пропадут из выдачи у всех, кроме администратора.',
        en: 'These employees still have clients. Proceeding now would leave the clients without an owner, invisible to everyone but an administrator.',
        vn: 'Những nhân viên này vẫn còn khách hàng. Nếu tiếp tục, khách hàng sẽ không có người phụ trách và biến mất khỏi danh sách của mọi người trừ quản trị viên.',
      }[l],
      clientsCount: {
        ru: 'клиентов: {count}',
        en: 'clients: {count}',
        vn: 'khách hàng: {count}',
      }[l],
      transfer: { ru: 'Передать', en: 'Hand over', vn: 'Bàn giao' }[l],
      transferFrom: {
        ru: 'Портфель сотрудника {name}',
        en: "{name}'s portfolio",
        vn: 'Danh mục của {name}',
      }[l],
      successor: { ru: 'Кому передать', en: 'Hand over to', vn: 'Bàn giao cho' }[l],
      successorPlaceholder: {
        ru: 'Выберите менеджера',
        en: 'Pick a manager',
        vn: 'Chọn quản lý',
      }[l],
      successorHint: {
        ru: 'В списке только активные менеджеры: отключённый принять портфель не может — клиенты осиротеют заново.',
        en: 'Only active managers are listed: a disabled one cannot take the portfolio — the clients would be orphaned again.',
        vn: 'Chỉ liệt kê quản lý đang hoạt động: người bị vô hiệu hóa không thể nhận danh mục.',
      }[l],
      confirmTransfer: {
        ru: 'Передать портфель',
        en: 'Hand over the portfolio',
        vn: 'Bàn giao danh mục',
      }[l],
      transferred: {
        ru: 'Передано клиентов: {count}',
        en: 'Clients handed over: {count}',
        vn: 'Đã bàn giao khách hàng: {count}',
      }[l],
      checkFailed: {
        ru: 'Не удалось проверить портфель: {reason}. Действие отменено.',
        en: 'Could not check the portfolio: {reason}. The action was cancelled.',
        vn: 'Không kiểm tra được danh mục: {reason}. Hành động đã bị hủy.',
      }[l],
    },
    invitations: {
      title: { ru: 'Приглашения', en: 'Invitations', vn: 'Lời mời' }[l],
      invite: { ru: 'Пригласить', en: 'Invite', vn: 'Mời' }[l],
      inviteTitle: {
        ru: 'Пригласить пользователя',
        en: 'Invite a user',
        vn: 'Mời người dùng',
      }[l],
      send: { ru: 'Отправить', en: 'Send', vn: 'Gửi' }[l],
      searchPlaceholder: {
        ru: 'Поиск по почте',
        en: 'Search by email',
        vn: 'Tìm theo email',
      }[l],
      truncated: {
        ru: 'Показаны не все приглашения: список слишком длинный. Уточните поиск.',
        en: 'Not all invitations are shown: the list is too long. Narrow the search.',
        vn: 'Chưa hiển thị hết lời mời: danh sách quá dài. Hãy thu hẹp tìm kiếm.',
      }[l],
      empty: {
        title: { ru: 'Приглашений нет', en: 'No invitations', vn: 'Chưa có lời mời' }[l],
        description: {
          ru: 'Никого не приглашали. Отправьте приглашение — сотрудник задаст пароль сам.',
          en: 'Nobody has been invited yet. Send an invitation and the person sets their own password.',
          vn: 'Chưa mời ai. Hãy gửi lời mời — nhân sự sẽ tự đặt mật khẩu.',
        }[l],
        filtered: {
          ru: 'По текущему запросу и фильтру приглашений нет.',
          en: 'No invitations match the current search and filter.',
          vn: 'Không có lời mời khớp với tìm kiếm và bộ lọc hiện tại.',
        }[l],
      },
      hint: {
        ru: 'Колонка «Приложение» показывает, куда именно придёт ссылка. Значение вычисляется из ролей и не редактируется.',
        en: 'The «App» column shows where the link will lead. The value is derived from roles and is not editable.',
        vn: 'Cột «Ứng dụng» cho biết liên kết sẽ dẫn tới đâu. Giá trị suy ra từ vai trò và không sửa được.',
      }[l],
      columns: {
        email: { ru: 'Почта', en: 'Email', vn: 'Email' }[l],
        roles: { ru: 'Роли', en: 'Roles', vn: 'Vai trò' }[l],
        app: { ru: 'Приложение', en: 'App', vn: 'Ứng dụng' }[l],
        status: { ru: 'Статус', en: 'Status', vn: 'Trạng thái' }[l],
        sent: { ru: 'Отправлено', en: 'Sent', vn: 'Đã gửi' }[l],
        invitedBy: { ru: 'Кем приглашён', en: 'Invited by', vn: 'Người mời' }[l],
        expires: { ru: 'Истекает', en: 'Expires', vn: 'Hết hạn' }[l],
        expiresIn: { ru: 'Истекает через', en: 'Expires in', vn: 'Hết hạn sau' }[l],
      },
      // Относительный срок в колонке «Истекает через»: «2 дн. 4 ч».
      days: { ru: '{n} дн.', en: '{n} d', vn: '{n} ngày' }[l],
      hours: { ru: '{n} ч', en: '{n} h', vn: '{n} giờ' }[l],
      expiresSoon: { ru: 'меньше часа', en: 'less than an hour', vn: 'dưới một giờ' }[l],
      status: {
        pending: { ru: 'Ожидает', en: 'Pending', vn: 'Đang chờ' }[l],
        accepted: { ru: 'Принято', en: 'Accepted', vn: 'Đã chấp nhận' }[l],
        expired: { ru: 'Истекло', en: 'Expired', vn: 'Hết hạn' }[l],
        revoked: { ru: 'Отозвано', en: 'Revoked', vn: 'Đã thu hồi' }[l],
      },
      filters: {
        any: { ru: 'Любой статус', en: 'Any status', vn: 'Mọi trạng thái' }[l],
      },
      actions: {
        resend: { ru: 'Отправить ещё', en: 'Resend', vn: 'Gửi lại' }[l],
        revoke: { ru: 'Отозвать', en: 'Revoke', vn: 'Thu hồi' }[l],
      },
      form: {
        locale: { ru: 'Язык письма', en: 'Email language', vn: 'Ngôn ngữ email' }[l],
        localeHint: {
          ru: 'Язык письма получателя, он не обязан совпадать с языком вашего интерфейса.',
          en: "The recipient's email language; it need not match your interface language.",
          vn: 'Ngôn ngữ email của người nhận, không nhất thiết trùng ngôn ngữ giao diện của bạn.',
        }[l],
        rolesRequired: {
          ru: 'Выберите хотя бы одну роль',
          en: 'Pick at least one role',
          vn: 'Chọn ít nhất một vai trò',
        }[l],
        addRole: { ru: 'Добавить роль…', en: 'Add a role…', vn: 'Thêm vai trò…' }[l],
        // Приложение производно от ролей — принцип ADR-0042, а не ограничение формы.
        appHint: {
          ru: 'Вычисляется из ролей, не редактируется. Определяет, куда придёт ссылка из письма.',
          en: 'Derived from roles, not editable. Determines where the emailed link leads.',
          vn: 'Suy ra từ vai trò, không sửa được. Quyết định liên kết trong email dẫn tới đâu.',
        }[l],
        appEmpty: {
          ru: 'Выберите роли',
          en: 'Pick roles first',
          vn: 'Hãy chọn vai trò',
        }[l],
      },
      // Названия языков письма: ключи ru/en/vn — это выбираемый язык, а не локаль
      // интерфейса, поэтому каждое название переводится на все три.
      locales: {
        ru: { ru: 'Русский', en: 'Russian', vn: 'Tiếng Nga' }[l],
        en: { ru: 'Английский', en: 'English', vn: 'Tiếng Anh' }[l],
        vn: { ru: 'Вьетнамский', en: 'Vietnamese', vn: 'Tiếng Việt' }[l],
      },
      toast: {
        sent: { ru: 'Приглашение отправлено', en: 'Invitation sent', vn: 'Đã gửi lời mời' }[l],
        revoked: {
          ru: 'Приглашение отозвано',
          en: 'Invitation revoked',
          vn: 'Đã thu hồi lời mời',
        }[l],
        resent: {
          ru: 'Письмо отправлено заново',
          en: 'Email sent again',
          vn: 'Đã gửi lại email',
        }[l],
      },
    },
  },
})

export const adminUsersMessages: Record<Locale, LocaleOverride> = {
  ru: build('ru'),
  en: build('en'),
  vn: build('vn'),
}
